import { differenceInSeconds, parseISO } from "date-fns";
import _isBoolean from "lodash/isBoolean";
import { dateFromDatetime, timeFromDatetime } from "misc/formatHelpers";
import {
  FieldChangeType,
  HistoryFilterValuesType,
  RecordHistoryRecordType,
} from "organisms/RecordHistory/RecordHistory.types";
import { RaRecord } from "react-admin";

export function adaptFirstHistoryEntry(
  entries: RecordHistoryRecordType[],
  record: RaRecord,
  resource: string,
): RecordHistoryRecordType[] {
  const isCreateEntry = (entry: RecordHistoryRecordType) => entry.action === "+" && entry.relation_name === null;
  const createEntryIndex = entries.findIndex(isCreateEntry);

  if (createEntryIndex === -1) {
    const superficialCreateEntry: RecordHistoryRecordType = {
      id: 0,
      action: "+",
      relation_name: null,
      occured_at: record.created_at as string,
      changed_by: undefined,
      resource,
    };

    return entries.concat(superficialCreateEntry);
  } else {
    return entries;
  }
}

function dateTimeChangeToDayChange(change: FieldChangeType): FieldChangeType | undefined {
  const { old_value, new_value } = change;
  const oldDay = dateFromDatetime(old_value as string);
  const newDay = dateFromDatetime(new_value as string);

  if (oldDay !== newDay) {
    return { name: "day", old_value, new_value, old_representation: null, new_representation: null };
  }
}

function transformedEventDateChanges(oldChanges: FieldChangeType[]): FieldChangeType[] {
  const isDatetimeChange = (c: FieldChangeType) => c.name === "start_datetime" || c.name === "end_datetime";
  const oldDatetimeChanges = oldChanges.filter(isDatetimeChange);
  const otherChanges = oldChanges.filter((c) => !isDatetimeChange(c));

  const newDatetimeChanges: FieldChangeType[] = [];

  oldDatetimeChanges.forEach((change) => {
    if (change.name === "start_datetime") {
      const dayChange = dateTimeChangeToDayChange(change);

      if (dayChange) {
        newDatetimeChanges.push(dayChange);
      }
    }

    const oldTime = timeFromDatetime(change.old_value as string);
    const newTime = timeFromDatetime(change.new_value as string);

    if (oldTime !== newTime) {
      newDatetimeChanges.push(change);
    }
  });

  return newDatetimeChanges.concat(otherChanges);
}

export function preprocessFieldChanges(
  relationName: string | null,
  fieldChanges: FieldChangeType[] = [],
): FieldChangeType[] {
  if (relationName === "dates") {
    return transformedEventDateChanges(fieldChanges);
  } else if (relationName === "prices") {
    return fieldChanges.filter((fc) => fc.name !== "hashed_positions");
  } else if (relationName === "bookings" || relationName === "invoices") {
    return [];
  } else {
    return fieldChanges;
  }
}

function getResourceFromRelationName(relationName: string | null, originResource: string): string {
  if (relationName === "dates") {
    return "event_dates";
  } else if (originResource === "orders" && relationName === "items") {
    return "order_items";
  } else if (
    originResource === "event_bases" &&
    (relationName === "group_children" || relationName === "group_parent")
  ) {
    return "event_bases";
  } else if (originResource === "events" && (relationName === "group_children" || relationName === "group_parents")) {
    return "events";
  } else if (relationName === "event_lecturers") {
    return "events";
  } else if (relationName === "rooms") {
    return "event_rooms";
  } else if (relationName === "price__positions") {
    return "price_positions";
  } else if (relationName === "lecturer__qualifications") {
    return "qualifications";
  } else if (relationName === "attendee__custom_fields_data") {
    return "custom_fields_data";
  } else if (
    relationName !== null &&
    ["attendee", "auth_user", "price", "moodle_user", "event_feedback_form", "lecturer"].includes(relationName)
  ) {
    return `${relationName}s`;
  } else {
    return relationName || originResource;
  }
}

function shouldBatchTimelineData(
  entry: RecordHistoryRecordType,
  nextEntry: RecordHistoryRecordType | undefined,
  type: string,
): boolean {
  const occuredAroundTheSameTime = (entry: RecordHistoryRecordType, nextEntry: RecordHistoryRecordType) =>
    differenceInSeconds(parseISO(entry.occured_at as string), parseISO(nextEntry.occured_at as string)) < 5 * 60;

  const additionalChecks = (entry: RecordHistoryRecordType, nextEntry: RecordHistoryRecordType) => {
    if (type === "emails") {
      if (entry.record?.email_job && nextEntry.record?.email_job) {
        return entry.record.email_job === nextEntry.record.email_job;
      }
      return !!(
        (entry.record?.external_template && entry.record.external_template === nextEntry.record?.external_template) ||
        (entry.record?.subject && entry.record.subject === nextEntry.record?.subject)
      );
    } else if (
      type === "event_attendances" &&
      (entry.action !== "+" || entry.record?.event_date_day !== nextEntry.record?.event_date_day)
    ) {
      return false;
    } else {
      return true;
    }
  };

  return !!(
    nextEntry &&
    nextEntry.relation_name === type &&
    entry.action === nextEntry.action &&
    occuredAroundTheSameTime(entry, nextEntry) &&
    additionalChecks(entry, nextEntry)
  );
}

// Batches a run of consecutive comparable entries, measuring the time window from the first one.
// Skippable entries inside the run are dropped without ending it.
function maybeBatchTimelinedata(
  timelineData: RecordHistoryRecordType[],
  index: number,
  entry: RecordHistoryRecordType,
  type: string,
): [RecordHistoryRecordType[], number] {
  const batchData = [entry];
  let newIndex = index + 1;

  while (shouldBatchTimelineData(entry, timelineData[newIndex], type)) {
    const nextEntry = timelineData[newIndex];

    if (!shouldSkipTimelineEntry(nextEntry)) {
      batchData.push(nextEntry);
    }

    newIndex++;
  }

  const newEntry = batchData.length > 1 ? { ...entry, batch_data: batchData } : entry;
  return [[newEntry], newIndex];
}

function shouldSkipTimelineEntry(entry: RecordHistoryRecordType): boolean {
  const notRecordCreateEntry = !(entry.relation_name === null && entry.action === "+");
  const attendeeCreateEntry = entry.relation_name === "attendee" && entry.action === "+";
  const priceCreateEntry = entry.relation_name === "price" && entry.action === "+";

  if (entry.relation_name === "emails" && entry.record && entry.record.is_test) {
    return true;
  }

  return notRecordCreateEntry && (attendeeCreateEntry || priceCreateEntry);
}

function prependNewEntries(
  originalEntry: RecordHistoryRecordType,
  changesToSplit: FieldChangeType[],
  otherChanges: FieldChangeType[],
): [RecordHistoryRecordType[], RecordHistoryRecordType | null] {
  const newEntries = changesToSplit.map((fc) => ({ ...originalEntry, field_changes: [fc] }));

  if (otherChanges.length > 0) {
    const updatedEntry = { ...originalEntry, field_changes: otherChanges };
    return [newEntries, updatedEntry];
  } else {
    return [newEntries, null];
  }
}

function splitChanges(
  entry: RecordHistoryRecordType,
  splitFunction: (fc: FieldChangeType) => boolean,
): [FieldChangeType[], FieldChangeType[]] {
  const fieldChanges = entry.field_changes ?? [];
  const changesToSplit = fieldChanges.filter(splitFunction);
  const otherChanges = fieldChanges.filter((fc) => !splitFunction(fc));

  return [changesToSplit, otherChanges];
}

function createEntriesForBooleanFieldChanges(
  entry: RecordHistoryRecordType,
): [RecordHistoryRecordType[], RecordHistoryRecordType | null] {
  const [booleanChanges, otherChanges] = splitChanges(entry, (fc) => _isBoolean(fc.new_value));
  return prependNewEntries(entry, booleanChanges, otherChanges);
}

function createEntriesForFieldChangesToSplit(
  entry: RecordHistoryRecordType | null,
  resource: string,
): [RecordHistoryRecordType[], RecordHistoryRecordType | null] {
  if (entry === null) return [[], null];
  let changesToSplit: FieldChangeType[] = [];
  let otherChanges: FieldChangeType[] = [];

  if (resource === "events" && entry.relation_name === null) {
    [changesToSplit, otherChanges] = splitChanges(entry, (fc) => fc.name === "status");
  } else if (resource === "event_bases") {
    [changesToSplit, otherChanges] = splitChanges(entry, (fc) => fc.name === "event_type");
  } else {
    otherChanges = entry.field_changes ?? [];
  }

  return prependNewEntries(entry, changesToSplit, otherChanges);
}

export function preprocessTimelineData(
  timelineData: RecordHistoryRecordType[],
  resource: string,
): RecordHistoryRecordType[] {
  const newTimelineData: RecordHistoryRecordType[] = [];

  for (let i = 0; i < timelineData.length; ) {
    const entry = { ...timelineData[i] };
    let entriesToAppend = [entry];
    let newIndex = i + 1;

    if (shouldSkipTimelineEntry(entry)) {
      i = newIndex;
      continue;
    } else if (entry.relation_name === "emails" && resource === "events") {
      [entriesToAppend, newIndex] = maybeBatchTimelinedata(timelineData, i, entry, "emails");
    } else if (entry.relation_name === "event_attendances") {
      [entriesToAppend, newIndex] = maybeBatchTimelinedata(timelineData, i, entry, "event_attendances");
    } else if (entry.action === "~") {
      const [booleanEntries, entryWithoutBooleanChanges] = createEntriesForBooleanFieldChanges(entry);
      const [splitEntries, entryWithoutSplitChanges] = createEntriesForFieldChangesToSplit(
        entryWithoutBooleanChanges,
        resource,
      );

      entriesToAppend = [...booleanEntries, ...splitEntries];

      if (entryWithoutSplitChanges) {
        entriesToAppend = entriesToAppend.concat(entryWithoutSplitChanges);
      }
    }

    entriesToAppend.forEach((entry) => {
      entry.resource = getResourceFromRelationName(entry.relation_name, resource);
      newTimelineData.push(entry);
    });

    i = newIndex;
  }

  return newTimelineData;
}

export function filterHistoryData(
  filterValues: HistoryFilterValuesType,
  data: RecordHistoryRecordType[],
): RecordHistoryRecordType[] | null {
  const { changedBy, from, to, resource } = filterValues;
  const changedById = parseInt(changedBy);

  if (!changedById && !from && !to && !resource) {
    return null;
  }

  const filterByResource = (resource: string, entry: RecordHistoryRecordType) => {
    if (!resource) return true;
    return !!entry.resource && entry.resource == resource;
  };

  const filterByUser = (changedById: number, entry: RecordHistoryRecordType) => {
    if (!changedById) return true;
    return !!entry.changed_by && entry.changed_by.id == changedById;
  };

  const filterByFrom = (from: string, entry: RecordHistoryRecordType) => {
    if (!from) return true;
    return parseISO(entry.occured_at as string) > parseISO(from);
  };

  const filterByTo = (to: string, entry: RecordHistoryRecordType) => {
    if (!to) return true;
    return parseISO(entry.occured_at as string) < parseISO(to);
  };

  const filterChain = (entry: RecordHistoryRecordType) =>
    filterByResource(resource, entry) &&
    filterByUser(changedById, entry) &&
    filterByFrom(from, entry) &&
    filterByTo(to, entry);

  return data.filter(filterChain);
}

export function hasTextHeavyChanges(resource: string | undefined, fieldChanges: FieldChangeType[] = []): boolean {
  return (
    resource === "descriptions" ||
    resource === "custom_fields_data" ||
    (resource === "notes" && !!fieldChanges.find((f) => f.name === "text")) ||
    (resource === "event_bases" && !!fieldChanges.find((f) => f.name === "summary"))
  );
}
